import { THIRTY_DAYS_SECONDS } from 'constants/attribution';
import { REFERRALS_ALLOWLISTED_COUNTRY_CODES } from 'constants/restrictedCountries';
import {
    getCookie,
    getLocalStorageWithExpiration,
    removeCookie,
    removeLocalStorageItem,
    setCookieWithExpiration,
    setLocalStorageWithExpiration,
} from 'utils/storage';

/**
 * The single owner of how a referral code is normalized, persisted, read, and cleared.
 * Nothing outside this module may touch the cookie or the localStorage key directly
 * (the one exception is `lib/gtmScript.ts`, which reads the cookie for the dataLayer).
 */

/** localStorage key. Deliberately shares the cookie's 30-day expiry — see readDurableReferral. */
const REFERRAL_STORAGE_KEY = 'kalshi_referral_code';
/** Cookie key — read by lib/gtmScript.ts, so its name and semantics are load-bearing. */
const REFERRAL_COOKIE_KEY = 'kalshi_referral';
const REFERRAL_QUERY_PARAM = 'referral';

// Backend limit: the rewards proto enforces max_len=50 runes, so a longer code can never validate.
const MAX_REFERRAL_CODE_LENGTH = 50;

/** Trim + empty->undefined + oversized->undefined. The ONLY normalization. No case changes (see constraint 3). */
export function normalizeReferralCode(raw: string | null | undefined): string | undefined {
    const trimmed = raw?.trim();
    if (!trimmed) {
        return undefined;
    }
    // Code points, not UTF-16 units, to match Go's rune counting.
    if ([...trimmed].length > MAX_REFERRAL_CODE_LENGTH) {
        return undefined;
    }
    return trimmed;
}

/** Reads `?referral=` from a search string (defaults to window.location.search). */
export function readReferralFromSearch(
    search = typeof window !== 'undefined' ? window.location.search : '',
): string | undefined {
    if (!search) {
        return undefined;
    }

    try {
        return normalizeReferralCode(new URLSearchParams(search).get(REFERRAL_QUERY_PARAM));
    } catch {
        return undefined;
    }
}

/**
 * At-capture durability. Writes localStorage only — NOT the cookie, because
 * lib/gtmScript.ts reads the cookie and must only ever observe validated codes.
 * Call this the moment a code is captured, before validation.
 */
export function persistCapturedReferral(code: string): void {
    setLocalStorageWithExpiration(REFERRAL_STORAGE_KEY, code, THIRTY_DAYS_SECONDS);
}

/**
 * Post-validation persistence. Writes the cookie (30d) AND refreshes localStorage.
 * Call only for a code that validated `valid === true`.
 */
export function persistValidatedReferral(code: string): void {
    setCookieWithExpiration(REFERRAL_COOKIE_KEY, code, THIRTY_DAYS_SECONDS);
    setLocalStorageWithExpiration(REFERRAL_STORAGE_KEY, code, THIRTY_DAYS_SECONDS);
}

/**
 * The durable read: localStorage ?? cookie. Both carriers use THIRTY_DAYS_SECONDS on
 * purpose — localStorage exists to survive a reload before validation, not to widen the
 * attribution window. Changing its expiry changes who gets credited a referral bonus.
 */
export function readDurableReferral(): string | undefined {
    return (
        normalizeReferralCode(getLocalStorageWithExpiration(REFERRAL_STORAGE_KEY)) ??
        normalizeReferralCode(getCookie(REFERRAL_COOKIE_KEY))
    );
}

/**
 * Cookie-aware clear. With `code`, removes each carrier ONLY if it holds that code —
 * so an unrelated bad code can no longer delete a different valid one (audit §2.2).
 * With no argument, clears unconditionally (explicit user removal).
 */
export function clearReferral(code?: string): void {
    if (code === undefined) {
        removeCookie(REFERRAL_COOKIE_KEY);
        removeLocalStorageItem(REFERRAL_STORAGE_KEY);
        return;
    }

    const target = normalizeReferralCode(code);
    if (normalizeReferralCode(getCookie(REFERRAL_COOKIE_KEY)) === target) {
        removeCookie(REFERRAL_COOKIE_KEY);
    }
    if (normalizeReferralCode(getLocalStorageWithExpiration(REFERRAL_STORAGE_KEY)) === target) {
        removeLocalStorageItem(REFERRAL_STORAGE_KEY);
    }
}

/**
 * THE submission-time resolver. Mirrors `resolveUtmDataForSubmission` in utils/attribution.ts.
 * Precedence: in-memory store -> current URL -> durable storage.
 * Every consumer that sends a referral to the backend MUST go through this.
 *
 * `invalidatedCode` excludes a code already proven invalid this session (e.g. from
 * `referralStore`'s `invalidatedReferralCode`) — clearing the store on an invalid verdict
 * doesn't remove the code from the URL, so without this the URL fallback would resend the same
 * code the lookup just rejected. Only that specific code is skipped; a different code still
 * sitting in the URL or durable storage is unaffected.
 */
export function resolveReferralForSubmission(
    storeReferral?: string | null,
    invalidatedCode?: string | null,
): string | undefined {
    const invalidated = normalizeReferralCode(invalidatedCode);
    const candidates = [normalizeReferralCode(storeReferral), readReferralFromSearch(), readDurableReferral()];
    return candidates.find((candidate): candidate is string => !!candidate && candidate !== invalidated);
}

/** Country-gated signup param. Moved verbatim from signupReferral.ts — same behavior. */
export function getReferralSignupParam(
    code: string | undefined,
    country: string | undefined,
): { referral_code: string } | undefined {
    return code && REFERRALS_ALLOWLISTED_COUNTRY_CODES.includes(country ?? '') ? { referral_code: code } : undefined;
}

/**
 * Country-gated resolver for callers outside React render (`oAuthLogin`, One Tap), where the
 * `useReferralForSubmission` hook can't be used.
 *
 * Fails OPEN on an unresolved country, unlike `getReferralSignupParam`: at the point `oAuthLogin`
 * builds its request, the geo lookup has often not resolved yet, and dropping the code there
 * would lose referrals on the busiest OAuth path. The backend enforces the allowlist
 * authoritatively, so an unknown country is safe to defer. A country known to be outside the
 * allowlist is still dropped.
 */
export function resolveEligibleReferralForSubmission(
    storeReferral: string | undefined | null,
    country: string | undefined,
    invalidatedCode?: string | null,
): string | undefined {
    const code = resolveReferralForSubmission(storeReferral, invalidatedCode);
    if (!code) return undefined;
    const knownCountry = country?.trim();
    if (knownCountry && !REFERRALS_ALLOWLISTED_COUNTRY_CODES.includes(knownCountry)) return undefined;
    return code;
}
